import {Alert, Avatar, Button, Rate, Space} from "antd";
import {
  CloseOutlined,
  DollarOutlined,
  EditOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {useSelector} from "react-redux";
import {
  StyledCustomerDashboardProductCard,
  StyledCustomerDashboardProductCardContent,
  StyledCustomerProductCardViewDetailsCreatedBy,
} from "./card.style";
import {StyledSessionDetailsDrawer} from "../../../calls/service/sales-and-insights/customers-section/style";
import {
  ReviewForm,
  ReviewFormProps,
} from "../../../calls/past-calls/past-call-card/customer";
import {
  GetCustomerProductsSingleProductResponse,
  sumCustomerPurchaseAddOns,
  useGetProductCustomer,
} from "../../../../react-query-hooks/products";
import {FullPageLoader} from "../../../full-page-loader";
import {useState} from "react";
import {ReviewOfferingType} from "../../../../react-query-hooks/reviews/types";
import {useCustomerIdentity} from "../../../../redux/auth/login/customer-identity";
import {StyledMediaLogsListItem} from "../../product-form/content-upload/content-upload.style";
import {
  // useGetCustomerCanEditReview,
  useGetCustomerReview,
} from "../../../../react-query-hooks/reviews/get-reviews";
import {
  StyledCustomerReviewBlock,
  StyledCustomerReviewBlockStarsAndDate,
  StyledCustomerReviewBlockStarsAndDateWrapper,
} from "../../../calls/past-calls/past-call-card/customer/styles";
import {
  StyledSessionDetailsDrawerBookingDetailsItem,
  StyledSessionDetailsDrawerBookingDetailsWrapper,
} from "../../../calls/service/sales-and-insights/customers-section/booking-details/style";
import {RootState} from "../../../../redux/store";

type TCustomerDashboardProductCard = {
  product: GetCustomerProductsSingleProductResponse;
};

export type CustomerProductCardViewDetailsDrawerProps = {
  title: string;
  onClose: () => void;
  isVisible: boolean;
  description: string;
  showReviewFormDrawer: () => void;
  offeringId: ReviewFormProps["offeringId"];
  offeringType: ReviewFormProps["offeringType"];
  /** Purchase reference — v2 keys the purchase detail by this, not by product id. */
  reference: string;
  redirectUrl: {
    href: string;
    name: string;
  } | null;
};

type ProductReviewFormDrawerProps = {
  title: string;
  onClose: () => void;
  isVisible: boolean;
  offeringId: ReviewFormProps["offeringId"];
  offeringType: ReviewFormProps["offeringType"];
  closeReviewFormDrawer: () => void;
};

export function CustomerDashboardProductCard({product}: TCustomerDashboardProductCard) {
  const [isViewDetailsDrawerOpen, setIsViewDetailsDrawerOpen] = useState<boolean>(false);
  const [isReviewFormDrawerOpen, setIsReviewFormDrawerOpen] = useState<boolean>(false);
  let stripedHtml = product.description.replace(/<[^>]+>/g, "");
  const productDescription = stripedHtml;

  const offeringId = product.id;
  const offeringType: ReviewOfferingType = "product";
  const drawerTitle = product.title;
  function handleRedirectLink() {
    if (product.url_redirect) {
      return product.url_redirect.startsWith("http")
        ? {href: product.url_redirect, name: product.url_redirect}
        : {name: product.url_redirect, href: `//${product.url_redirect}`};
    } else {
      return null;
    }
  }

  const redirectUrl = handleRedirectLink();

  const showViewDetailsDrawer = () => {
    setIsViewDetailsDrawerOpen(true);
  };
  const closeViewDetailsDrawer = () => {
    setIsViewDetailsDrawerOpen(false);
  };

  const showReviewFormDrawer = () => {
    setIsReviewFormDrawerOpen(true);
  };
  const closeReviewFormDrawer = () => {
    setIsReviewFormDrawerOpen(false);
  };

  return (
    <StyledCustomerDashboardProductCard title={product.title}>
      <StyledCustomerDashboardProductCardContent>
        <div className="product-card-image-wrapper">
          <img
            className="product-card-image"
            src={
              product.coverImage
                ? product.coverImage
                : product.cover_images && product.cover_images.length > 0
                ? product.cover_images[0].media_url
                : "/images/no-image-placeholder.png"
            }
            alt="product"
          />
        </div>
        <p className="product-card-info-text">{productDescription}</p>
        <div className="product-card-extra-info">
          <div>
            <Space size={10}>
              <DollarOutlined style={{color: "#222222"}} />
              {product.amount < 1 ? (
                "Free"
              ) : (
                <span>{`${product.currency} ${product.amount}`}</span>
              )}
            </Space>
          </div>
        </div>
        <div className="product-card-start-btn">
          <Button type="primary" size="small" onClick={showViewDetailsDrawer}>
            View details
          </Button>
        </div>
      </StyledCustomerDashboardProductCardContent>
      {isViewDetailsDrawerOpen && (
        <CustomerProductCardViewDetailsDrawer
          isVisible={isViewDetailsDrawerOpen}
          onClose={closeViewDetailsDrawer}
          title={drawerTitle}
          description={productDescription}
          offeringId={offeringId}
          offeringType={offeringType}
          reference={product.reference}
          showReviewFormDrawer={showReviewFormDrawer}
          redirectUrl={redirectUrl}
        />
      )}
      {isReviewFormDrawerOpen && (
        <ProductReviewFormDrawer
          isVisible={isReviewFormDrawerOpen}
          onClose={closeReviewFormDrawer}
          title={drawerTitle}
          offeringId={offeringId}
          offeringType={offeringType}
          closeReviewFormDrawer={closeReviewFormDrawer}
        />
      )}
    </StyledCustomerDashboardProductCard>
  );
}

export function CustomerProductCardViewDetailsDrawer({
  title,
  isVisible,
  onClose,
  description,
  offeringId,
  offeringType,
  reference,
  showReviewFormDrawer,
  redirectUrl,
}: CustomerProductCardViewDetailsDrawerProps) {
  const getProductCustomer = useGetProductCustomer(reference);
  const {emailAddress} = useCustomerIdentity();
  const getCustomerReview = useGetCustomerReview({
    emailAddress: emailAddress,
    offering_id: offeringId,
    offering_type: offeringType,
  });
  // const getCustomerCanEditReview = useGetCustomerCanEditReview({
  //   emailAddress: emailAddress,
  //   offering_id: offeringId,
  //   offering_type: offeringType,
  // });

  function handleFileDownload(url: string, fileName: string) {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
  }

  const purchase = getProductCustomer.data;
  const addOns = purchase?.addOns ?? [];
  // v2 charges the add-ons as part of the purchase amount and returns no
  // `total`, so this is the add-on share of that amount, not a grand total.
  const addOnsSubtotal = sumCustomerPurchaseAddOns(addOns);

  return (
    <StyledSessionDetailsDrawer
      title={title}
      placement="right"
      onClose={onClose}
      visible={isVisible}
      closable={false}
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
        ></Button>
      }
      footer={
        getCustomerReview.isSuccess ? null : (
          <>
            <Button size="small" type="primary" onClick={showReviewFormDrawer}>
              Leave a review
            </Button>
          </>
        )
      }
    >
      <div>
        {getProductCustomer.isLoading ? (
          <FullPageLoader />
        ) : getProductCustomer.isError || !purchase ? (
          <Alert message={"Error fetching product details"} type="error" />
        ) : (
          <Space direction="vertical" style={{display: "flex"}} size={24}>
            <StyledSessionDetailsDrawerBookingDetailsWrapper>
              <StyledSessionDetailsDrawerBookingDetailsItem>
                <p className="item-title">Amount paid</p>
                <p className="item-value">
                  {`${purchase.currency} ${purchase.amount.toLocaleString()}`}
                </p>
              </StyledSessionDetailsDrawerBookingDetailsItem>
              {/* Purchases made before v2 started recording the options the
                  customer selected come back with an empty list, so the whole
                  add-ons block is skipped rather than rendered empty. */}
              {addOns.length > 0 && (
                <div>
                  <div>
                    <p>Add-ons</p>
                    <ul>
                      {addOns.map((addOn) =>
                        addOn.options.map((option) => (
                          <li key={option.id}>
                            <StyledSessionDetailsDrawerBookingDetailsItem>
                              <p className="item-title">{`${addOn.variantName} - ${option.optionName}`}</p>
                              <p className="item-value">
                                {`${
                                  option.currency || purchase.currency
                                } ${option.price.toLocaleString()}`}
                              </p>
                            </StyledSessionDetailsDrawerBookingDetailsItem>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                  <StyledSessionDetailsDrawerBookingDetailsItem>
                    <p className="item-title">Add-ons subtotal</p>
                    <p className="item-value">
                      {`${purchase.currency} ${addOnsSubtotal.toLocaleString()}`}
                    </p>
                  </StyledSessionDetailsDrawerBookingDetailsItem>
                </div>
              )}
            </StyledSessionDetailsDrawerBookingDetailsWrapper>
            {redirectUrl && (
              <div>
                <p>
                  Your purchase includes a link:{" "}
                  <a
                    href={redirectUrl.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{fontWeight: 600, textDecoration: "underline"}}
                  >
                    {redirectUrl.name}
                  </a>
                </p>
              </div>
            )}

            <Space direction="vertical" style={{display: "flex"}} size={24}>
              {purchase.assets.length < 1 ? (
                <>
                  <p>No content available</p>
                </>
              ) : (
                purchase.assets.map((item) => (
                  <StyledMediaLogsListItem key={item.id}>
                    <p className="media-log-title">{item.fileName}</p>
                    <p>{item.fileSize}</p>
                    <div>
                      <Button
                        onClick={() => handleFileDownload(item.mediaUrl, item.fileName)}
                        size="small"
                      >
                        Download
                      </Button>
                    </div>
                  </StyledMediaLogsListItem>
                ))
              )}
            </Space>
            <StyledCustomerProductCardViewDetailsCreatedBy>
              <h2 className="host-heading-text">Created by</h2>
              <div className="host-info-wrapper">
                <div className="host-avatar-wrapper">
                  {purchase.coach.profilePhoto ? (
                    <Avatar size={50} src={purchase.coach.profilePhoto} />
                  ) : (
                    <Avatar size={50} icon={<UserOutlined />} />
                  )}
                </div>
                <div className="host-name-info-wrapper">
                  <p className="host-name">{purchase.coach.coachDisplayName}</p>
                </div>
              </div>
            </StyledCustomerProductCardViewDetailsCreatedBy>
            <p>{description}</p>
            <div>
              <Space direction="vertical" style={{display: "flex"}} size={16}>
                {getCustomerReview.isLoading ? (
                  <FullPageLoader />
                ) : getCustomerReview.isError ? null : (
                  <>
                    <p
                      style={{
                        fontWeight: 600,
                        fontSize: "1.6rem",
                      }}
                    >
                      Your review
                    </p>
                    <StyledCustomerReviewBlock>
                      <Space direction="vertical" style={{display: "flex"}} size={16}>
                        <StyledCustomerReviewBlockStarsAndDateWrapper>
                          <StyledCustomerReviewBlockStarsAndDate>
                            <Rate
                              disabled
                              defaultValue={getCustomerReview.data.rating}
                              value={getCustomerReview.data.rating}
                              style={{color: "#99C78F"}}
                              allowHalf
                            />
                            <p>
                              {new Date(getCustomerReview.data.updatedAt).toDateString()}
                            </p>
                          </StyledCustomerReviewBlockStarsAndDate>
                          {/* {getCustomerCanEditReview.data?.canEdit && ( */}
                          <Button
                            onClick={showReviewFormDrawer}
                            size="small"
                            type="text"
                            icon={<EditOutlined style={{fontSize: "1.6rem"}} />}
                          />
                          {/* )} */}
                        </StyledCustomerReviewBlockStarsAndDateWrapper>
                        <p>{getCustomerReview.data.review}</p>
                      </Space>
                    </StyledCustomerReviewBlock>
                  </>
                )}
              </Space>
            </div>
          </Space>
        )}
      </div>
    </StyledSessionDetailsDrawer>
  );
}
export function ProductReviewFormDrawer({
  title,
  isVisible,
  onClose,
  offeringId,
  offeringType,
  closeReviewFormDrawer,
}: ProductReviewFormDrawerProps) {
  const {emailAddress, firstName, lastName} = useCustomerIdentity();
  const getCustomerReview = useGetCustomerReview({
    emailAddress: emailAddress,
    offering_id: offeringId,
    offering_type: offeringType,
  });
  // const getCustomerCanEditReview = useGetCustomerCanEditReview({
  //   emailAddress: emailAddress,
  //   offering_id: offeringId,
  //   offering_type: offeringType,
  // });
  return (
    <StyledSessionDetailsDrawer
      title={title}
      placement="right"
      onClose={onClose}
      visible={isVisible}
      closable={false}
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
        ></Button>
      }
      footer={null}
    >
      <div>
        <Space direction="vertical" style={{display: "flex"}} size={24}>
          <ReviewForm
            reviewData={getCustomerReview.data}
            offeringId={offeringId}
            offeringType={offeringType}
            closeReviewFormDrawer={closeReviewFormDrawer}
            // canEdit={getCustomerCanEditReview.data?.canEdit}
            customerFirstName={firstName}
            customerLastName={lastName}
            customerEmail={emailAddress}
          />
        </Space>
      </div>
    </StyledSessionDetailsDrawer>
  );
}
